import { NavLink, Outlet } from 'react-router-dom';
import { Bell, CreditCard, Lock, User, Users } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

import { cn } from '@/lib/utils';
import { useCustomer } from '@/hooks/useCustomer';

/**
 * /settings — the single home for all customer settings.
 *
 * A tabbed hub (Profile / Team / Notifications / Security) with nested routes
 * under /settings/*. The former standalone /account page now lives here as the
 * Profile tab (and /account redirects to /settings/profile).
 *
 * Each tab manages its own data + admin gating; this shell just provides the
 * header band and the tab strip, mirroring the admin per-customer detail tabs.
 */

const TABS: ReadonlyArray<{ to: string; label: string; icon: LucideIcon }> = [
  { to: '/settings/profile', label: 'Profile', icon: User },
  { to: '/settings/team', label: 'Team', icon: Users },
  { to: '/settings/notifications', label: 'Notifications & mailing', icon: Bell },
  { to: '/settings/payments', label: 'Payment methods', icon: CreditCard },
  { to: '/settings/security', label: 'Security', icon: Lock },
];

export default function Settings() {
  const { data: ctx } = useCustomer();
  const customerName = ctx?.customer.name ?? 'Your account';

  return (
    <div className="scrollbar-thin h-full overflow-y-auto pb-20 md:pb-10">
      <div className="mx-auto max-w-3xl space-y-6 px-4 pb-12 pt-6 md:px-8">
        {/* Header band */}
        <div className="overflow-hidden rounded-2xl border-2 border-brix-primary-deep bg-brix-surface shadow-card-deep">
          <div className="bg-brix-primary-deep px-6 py-5 md:px-8">
            <p className="text-xs font-semibold tracking-tight text-white/70">Settings</p>
            <h1 className="mt-0.5 break-words font-display text-xl font-bold leading-tight tracking-tight text-white md:text-2xl">
              {customerName}
            </h1>
          </div>

          {/* Tab strip */}
          <nav
            aria-label="Settings"
            className="flex flex-wrap gap-1 border-t border-white/10 bg-brix-surface px-3 py-2"
          >
            {TABS.map((t) => {
              const Icon = t.icon;
              return (
                <NavLink
                  key={t.to}
                  to={t.to}
                  className={({ isActive }) =>
                    cn(
                      'inline-flex items-center gap-2 rounded-lg px-3.5 py-2 text-sm transition-colors duration-200',
                      isActive
                        ? 'bg-brix-primary-tint font-semibold text-brix-primary'
                        : 'font-medium text-brix-ink hover:bg-brix-primary-tint/60',
                    )
                  }
                >
                  <Icon size={15} /> {t.label}
                </NavLink>
              );
            })}
          </nav>
        </div>

        <Outlet />
      </div>
    </div>
  );
}
